import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import apiClient, { getErrorMessage } from '../../../api/client';
import { useToast } from '../../../hooks/useToast';
import { FiSave, FiSend, FiMail, FiMessageSquare } from 'react-icons/fi';

const EmailSmsSettings = () => {
  const { success, error: showError } = useToast();
  const [loading, setLoading] = useState(true);

  const [smtpForm, setSmtpForm] = useState({
    label: 'Default', host: '', port: 587, username: '', password: '',
    encryption: 'tls', fromEmail: '', fromName: '', replyToEmail: '',
  });
  const [smtpConfigured, setSmtpConfigured] = useState(false);
  const [smtpSaving, setSmtpSaving] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testingEmail, setTestingEmail] = useState(false);

  const [smsForm, setSmsForm] = useState({ apiKey: '', senderId: '' });
  const [smsConfigured, setSmsConfigured] = useState(false);
  const [smsSaving, setSmsSaving] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [testMessage, setTestMessage] = useState('This is a test message from Effective Global Staff.');
  const [testingSms, setTestingSms] = useState(false);

  useEffect(() => {
    Promise.all([
      apiClient.get('/comms/smtp').catch(() => ({ data: { data: [] } })),
      apiClient.get('/comms/sms/config').catch(() => ({ data: { data: null } })),
    ]).then(([smtpRes, smsRes]) => {
      const existingSmtp = smtpRes.data.data?.[0];
      if (existingSmtp) {
        setSmtpForm({
          label: existingSmtp.label || 'Default',
          host: existingSmtp.host || '',
          port: existingSmtp.port || 587,
          username: existingSmtp.username || '',
          password: '',
          encryption: existingSmtp.encryption || 'tls',
          fromEmail: existingSmtp.fromEmail || '',
          fromName: existingSmtp.fromName || '',
          replyToEmail: existingSmtp.replyToEmail || '',
        });
        setSmtpConfigured(true);
      }
      if (smsRes.data.data) {
        setSmsForm({ apiKey: '', senderId: smsRes.data.data.senderId || '' });
        setSmsConfigured(true);
      }
    }).finally(() => setLoading(false));
  }, []);

  const handleSmtpSave = async (e) => {
    e.preventDefault();
    setSmtpSaving(true);
    try {
      const payload = { ...smtpForm, isDefault: true };
      if (!payload.password) delete payload.password; // don't overwrite with blank
      if (!payload.replyToEmail) delete payload.replyToEmail;
      await apiClient.post('/comms/smtp', payload);
      success('✅ Email settings saved');
      setSmtpConfigured(true);
      setSmtpForm((p) => ({ ...p, password: '' }));
    } catch (err) {
      showError(getErrorMessage(err, 'Could not save email settings'));
    } finally {
      setSmtpSaving(false);
    }
  };

  const handleTestEmail = async (e) => {
    e.preventDefault();
    setTestingEmail(true);
    try {
      const { data } = await apiClient.post('/comms/smtp/test', { toEmail: testEmail });
      if (data.data.success) {
        success('✅ Test email sent — check the inbox');
      } else {
        showError(data.data.error || 'Test email failed to send');
      }
    } catch (err) {
      showError(getErrorMessage(err, 'Could not send test email'));
    } finally {
      setTestingEmail(false);
    }
  };

  const handleSmsSave = async (e) => {
    e.preventDefault();
    setSmsSaving(true);
    try {
      await apiClient.post('/comms/sms/config', smsForm);
      success('✅ SMS settings saved');
      setSmsConfigured(true);
      setSmsForm((p) => ({ ...p, apiKey: '' }));
    } catch (err) {
      showError(getErrorMessage(err, 'Could not save SMS settings'));
    } finally {
      setSmsSaving(false);
    }
  };

  const handleTestSms = async (e) => {
    e.preventDefault();
    setTestingSms(true);
    try {
      const { data } = await apiClient.post('/comms/sms/test', { toPhoneNumber: testPhone, message: testMessage });
      if (data.data.success) {
        success('✅ Test SMS sent');
      } else {
        showError(data.data.error || 'Test SMS failed to send');
      }
    } catch (err) {
      showError(getErrorMessage(err, 'Could not send test SMS'));
    } finally {
      setTestingSms(false);
    }
  };

  if (loading) return <div className="dashboard-page"><p>Loading...</p></div>;

  return (
    <motion.div className="dashboard-page" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
      <div className="page-header">
        <h1>Email & SMS Settings</h1>
        <p>Configure how this organisation sends outbound email and SMS</p>
      </div>

      <section className="page-section">
        <div className="section-header">
          <h2><FiMail size={18} style={{ verticalAlign: 'middle', marginRight: '0.5rem' }} />Email (SMTP)</h2>
          {smtpConfigured && <span className="badge" style={{ background: '#10b981', color: 'white' }}>Configured</span>}
        </div>
        <form onSubmit={handleSmtpSave} className="form-grid">
          <div className="form-field">
            <label>Label</label>
            <input value={smtpForm.label} onChange={(e) => setSmtpForm(p => ({ ...p, label: e.target.value }))} />
          </div>
          <div className="form-field">
            <label>SMTP Host *</label>
            <input required value={smtpForm.host} onChange={(e) => setSmtpForm(p => ({ ...p, host: e.target.value }))} placeholder="smtp.gmail.com" />
          </div>
          <div className="form-field">
            <label>Port *</label>
            <input type="number" required value={smtpForm.port} onChange={(e) => setSmtpForm(p => ({ ...p, port: Number(e.target.value) }))} />
          </div>
          <div className="form-field">
            <label>Encryption</label>
            <select value={smtpForm.encryption} onChange={(e) => setSmtpForm(p => ({ ...p, encryption: e.target.value }))}>
              <option value="tls">TLS</option>
              <option value="ssl">SSL</option>
              <option value="none">None</option>
            </select>
          </div>
          <div className="form-field">
            <label>Username</label>
            <input value={smtpForm.username} onChange={(e) => setSmtpForm(p => ({ ...p, username: e.target.value }))} />
          </div>
          <div className="form-field">
            <label>Password {smtpConfigured && <span style={{ fontWeight: 400, textTransform: 'none' }}>(leave blank to keep current)</span>}</label>
            <input type="password" value={smtpForm.password} onChange={(e) => setSmtpForm(p => ({ ...p, password: e.target.value }))} />
          </div>
          <div className="form-field">
            <label>From Email *</label>
            <input type="email" required value={smtpForm.fromEmail} onChange={(e) => setSmtpForm(p => ({ ...p, fromEmail: e.target.value }))} />
          </div>
          <div className="form-field">
            <label>From Name</label>
            <input value={smtpForm.fromName} onChange={(e) => setSmtpForm(p => ({ ...p, fromName: e.target.value }))} />
          </div>
          <div className="form-field">
            <label>Reply-To Email</label>
            <input type="email" value={smtpForm.replyToEmail} onChange={(e) => setSmtpForm(p => ({ ...p, replyToEmail: e.target.value }))} />
          </div>
          <div className="form-field full-width form-actions">
            <button type="submit" className="btn btn-primary" disabled={smtpSaving} style={{ width: 'auto' }}>
              <FiSave size={16} /> {smtpSaving ? 'Saving...' : 'Save Email Settings'}
            </button>
          </div>
        </form>

        {smtpConfigured && (
          <form onSubmit={handleTestEmail} style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #e5e7eb' }}>
            <div className="form-field" style={{ flex: 1, marginBottom: 0 }}>
              <label>Send Test Email To</label>
              <input type="email" required value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="you@example.com" />
            </div>
            <button type="submit" className="btn btn-outline" disabled={testingEmail} style={{ width: 'auto' }}>
              <FiSend size={16} /> {testingEmail ? 'Sending...' : 'Send Test'}
            </button>
          </form>
        )}
      </section>

      <section className="page-section">
        <div className="section-header">
          <h2><FiMessageSquare size={18} style={{ verticalAlign: 'middle', marginRight: '0.5rem' }} />SMS (Termii)</h2>
          {smsConfigured && <span className="badge" style={{ background: '#10b981', color: 'white' }}>Configured</span>}
        </div>
        <form onSubmit={handleSmsSave} className="form-grid">
          <div className="form-field">
            <label>Termii API Key {smsConfigured && <span style={{ fontWeight: 400, textTransform: 'none' }}>(leave blank to keep current)</span>}</label>
            <input type="password" required={!smsConfigured} value={smsForm.apiKey} onChange={(e) => setSmsForm(p => ({ ...p, apiKey: e.target.value }))} />
          </div>
          <div className="form-field">
            <label>Sender ID *</label>
            <input required value={smsForm.senderId} onChange={(e) => setSmsForm(p => ({ ...p, senderId: e.target.value }))} placeholder="EGSPayroll" maxLength={20} />
          </div>
          <div className="form-field full-width form-actions">
            <button type="submit" className="btn btn-primary" disabled={smsSaving} style={{ width: 'auto' }}>
              <FiSave size={16} /> {smsSaving ? 'Saving...' : 'Save SMS Settings'}
            </button>
          </div>
        </form>

        {smsConfigured && (
          <form onSubmit={handleTestSms} style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #e5e7eb' }}>
            <div className="form-grid">
              <div className="form-field">
                <label>Send Test SMS To</label>
                <input required value={testPhone} onChange={(e) => setTestPhone(e.target.value)} placeholder="+44..." />
              </div>
              <div className="form-field full-width">
                <label>Message</label>
                <input required value={testMessage} onChange={(e) => setTestMessage(e.target.value)} maxLength={500} />
              </div>
              <div className="form-field full-width form-actions">
                <button type="submit" className="btn btn-outline" disabled={testingSms} style={{ width: 'auto' }}>
                  <FiSend size={16} /> {testingSms ? 'Sending...' : 'Send Test'}
                </button>
              </div>
            </div>
          </form>
        )}
      </section>
    </motion.div>
  );
};

export default EmailSmsSettings;